import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Trophy, Star, Flame, Award, Heart, Eye, Zap, Users, Radio,
  TrendingUp, DollarSign, Gem, CheckCircle, Clock, Flag, UserCheck,
  FileText, PenTool, Banknote, Sparkles, X,
} from 'lucide-react';

const BADGE_ICONS = {
  'pen-tool': PenTool, 'file-text': FileText, heart: Heart, eye: Eye,
  zap: Zap, users: Users, flame: Flame, award: Award, radio: Radio,
  'trending-up': TrendingUp, star: Star, 'dollar-sign': DollarSign,
  banknote: Banknote, gem: Gem, 'check-circle': CheckCircle, clock: Clock,
  flag: Flag, 'user-check': UserCheck,
};

// --- XP Floating Toast ---
export function XpToast({ xp, action, onDone }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(onDone, 400);
    }, 2200);
    return () => clearTimeout(t);
  }, [onDone]);

  const label = action?.replace(/_/g, ' ') || 'action';

  return (
    <div
      className={`transition-all duration-400 ${
        visible ? 'translate-x-0 opacity-100' : 'translate-x-24 opacity-0'
      }`}
      data-testid="xp-toast"
    >
      <div className="flex items-center gap-2.5 bg-background/90 border border-violet-500/40 backdrop-blur-xl rounded-xl px-4 py-2.5 shadow-lg shadow-violet-500/10">
        <Sparkles className="w-4 h-4 text-violet-400 animate-pulse" />
        <span className="text-sm font-bold text-violet-300">+{xp} XP</span>
        <span className="text-xs text-muted-foreground/60 capitalize">{label}</span>
      </div>
    </div>
  );
}

// --- Level Up Overlay ---
export function LevelUpOverlay({ level, rank, rankColor, onClose }) {
  const [phase, setPhase] = useState('enter'); // enter → show → exit

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('show'), 50);
    const t2 = setTimeout(() => setPhase('exit'), 4000);
    const t3 = setTimeout(onClose, 4500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onClose]);

  return createPortal(
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center transition-all duration-500 ${
        phase === 'enter' ? 'bg-black/0' : phase === 'show' ? 'bg-background/70' : 'bg-black/0'
      }`}
      onClick={() => { setPhase('exit'); setTimeout(onClose, 500); }}
      data-testid="level-up-overlay"
    >
      <div
        className={`relative flex flex-col items-center transition-all duration-500 ${
          phase === 'show' ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
        }`}
      >
        {/* Particle rings */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-56 h-56 rounded-full border-2 border-violet-500/30 animate-ping" style={{ animationDuration: '2s' }} />
          <div className="absolute w-40 h-40 rounded-full border border-fuchsia-400/20 animate-ping" style={{ animationDuration: '1.5s', animationDelay: '0.3s' }} />
        </div>

        {/* Glow */}
        <div
          className="absolute w-48 h-48 rounded-full blur-3xl opacity-40"
          style={{ background: rankColor || '#8b5cf6' }}
        />

        {/* Level number */}
        <div
          className="relative w-28 h-28 rounded-2xl flex items-center justify-center mb-4 shadow-2xl"
          style={{
            background: `linear-gradient(135deg, ${rankColor || '#8b5cf6'}40, ${rankColor || '#8b5cf6'}15)`,
            border: `2px solid ${rankColor || '#8b5cf6'}60`,
          }}
        >
          <span className="text-5xl font-black" style={{ color: rankColor || '#8b5cf6' }}>
            {level}
          </span>
        </div>

        <h2 className="text-2xl font-black text-foreground mb-1 tracking-tight">LEVEL UP!</h2>
        <p className="text-lg font-semibold" style={{ color: rankColor || '#8b5cf6' }}>{rank}</p>
        <p className="text-xs text-muted-foreground/60 mt-2">Tap to dismiss</p>

        {/* Sparkle particles */}
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full animate-float-particle"
            style={{
              background: rankColor || '#8b5cf6',
              top: `${20 + Math.random() * 60}%`,
              left: `${10 + Math.random() * 80}%`,
              animationDelay: `${i * 0.2}s`,
              animationDuration: `${1.5 + Math.random()}s`,
            }}
          />
        ))}
      </div>
    </div>,
    document.body
  );
}

// --- Badge Unlock Overlay ---
export function BadgeUnlockOverlay({ badge, onClose }) {
  const [phase, setPhase] = useState('enter');
  const Icon = BADGE_ICONS[badge?.icon] || Award;

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('show'), 50);
    const t2 = setTimeout(() => setPhase('exit'), 3500);
    const t3 = setTimeout(onClose, 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onClose]);

  return createPortal(
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center transition-all duration-500 ${
        phase === 'enter' ? 'bg-black/0' : phase === 'show' ? 'bg-background/60' : 'bg-black/0'
      }`}
      onClick={() => { setPhase('exit'); setTimeout(onClose, 500); }}
      data-testid="badge-unlock-overlay"
    >
      <div
        className={`relative flex flex-col items-center transition-all duration-500 ${
          phase === 'show' ? 'scale-100 opacity-100 translate-y-0' : phase === 'enter' ? 'scale-75 opacity-0 translate-y-8' : 'scale-75 opacity-0 -translate-y-8'
        }`}
      >
        {/* Badge glow */}
        <div className="absolute w-32 h-32 rounded-full bg-amber-500/30 blur-3xl" />

        {/* Badge icon container */}
        <div className="relative w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-500/30 to-orange-600/20 border-2 border-amber-400/40 flex items-center justify-center mb-4 shadow-2xl shadow-amber-500/20">
          <Icon className="w-10 h-10 text-amber-400" />
          {/* Shine effect */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden">
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
              style={{ animation: 'badgeShine 1.5s ease-in-out infinite' }}
            />
          </div>
        </div>

        <h2 className="text-xl font-black text-foreground mb-1">BADGE UNLOCKED!</h2>
        <p className="text-lg font-bold text-amber-400">{badge?.name}</p>
        <p className="text-sm text-muted-foreground/70 mt-1 max-w-xs text-center">{badge?.description}</p>
        <p className="text-xs text-muted-foreground/50 mt-3">Tap to dismiss</p>
      </div>
    </div>,
    document.body
  );
}

// --- Notification Manager ---
export function GamificationNotificationManager({ queue, onProcessed }) {
  const [current, setCurrent] = useState(null);

  const processNext = useCallback(() => {
    setCurrent(null);
    onProcessed();
  }, [onProcessed]);

  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
    }
  }, [queue, current]);

  if (!current) return null;

  if (current.type === 'level_up') {
    return (
      <LevelUpOverlay
        level={current.level}
        rank={current.rank}
        rankColor={current.rankColor}
        onClose={processNext}
      />
    );
  }

  if (current.type === 'badge') {
    return (
      <BadgeUnlockOverlay
        badge={current.badge}
        onClose={processNext}
      />
    );
  }

  if (current.type === 'xp') {
    return (
      <XpToast
        xp={current.xp}
        action={current.action}
        onDone={processNext}
      />
    );
  }

  return null;
}
